// /metrics — list of user-defined metric preferences for the
// current org. Lets the user add a new one or remove an existing one.

import { useCallback, useEffect, useState } from "react";
import {
  deleteMetricPreference,
  listMetricPreferences,
  saveMetricPreference,
  type MetricPreference,
} from "../../api/metric_preferences";

interface Props {
  bearer: string;
  organization: string;
}

export function MetricsPage({ bearer, organization }: Props) {
  const [items, setItems] = useState<MetricPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [phrase, setPhrase] = useState("");
  const [description, setDescription] = useState("");
  const [rewritten, setRewritten] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = useCallback(() => {
    if (!organization) return;
    setLoading(true);
    listMetricPreferences(bearer, organization)
      .then((data) => {
        setItems(data);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [bearer, organization]);

  useEffect(() => reload(), [reload]);

  const onSave = async () => {
    if (!phrase.trim() || !rewritten.trim()) {
      setError("A metric phrase and a rewritten query are required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await saveMetricPreference(bearer, {
        organization,
        metric_phrase: phrase.trim(),
        description: description.trim(),
        rewritten_query: rewritten.trim(),
      });
      setPhrase("");
      setDescription("");
      setRewritten("");
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-line bg-white">
        <div className="max-w-3xl mx-auto px-6 h-[60px] flex items-center gap-3">
          <a href="/" className="flex items-center gap-2 text-neutral-700 hover:text-brand">
            <span className="h-7 w-7 rounded-full bg-brand text-white flex items-center justify-center font-bold text-sm">a</span>
            <span className="font-semibold">Agami</span>
          </a>
          <span className="text-neutral-300">/</span>
          <span className="text-neutral-900 font-medium">Metrics</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        <div className="space-y-3 rounded-[15px] border border-line p-5 bg-white">
          <div className="text-[15px] font-semibold text-neutral-900">Define a new metric</div>
          <div className="text-[13px] text-neutral-500">
            Any phrase here turns into a saved definition the model uses next time anyone in your org asks the same thing.
          </div>
          <input
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            placeholder="Metric phrase, e.g. MTBF"
            className="w-full h-10 px-3 rounded-full border border-line text-[14px] outline-none focus:ring-1 focus:ring-brand"
          />
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Plain-English definition (optional)"
            className="w-full h-10 px-3 rounded-full border border-line text-[14px] outline-none focus:ring-1 focus:ring-brand"
          />
          <textarea
            value={rewritten}
            onChange={(e) => setRewritten(e.target.value)}
            placeholder="When someone asks about this, answer: …"
            rows={3}
            className="w-full text-[14px] rounded-md border border-line p-2.5 outline-none focus:ring-1 focus:ring-brand resize-none"
          />
          {error && <div className="text-[12px] text-red-600">{error}</div>}
          <div className="flex justify-end">
            <button type="button" onClick={onSave} disabled={saving} className="btn-primary h-9 px-4 text-[13px]">
              {saving ? "Saving…" : "Save metric definition"}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-[13px] uppercase tracking-wide text-neutral-400">Your metrics</div>
          {loading ? (
            <div className="text-sm text-neutral-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-neutral-500">No saved metric definitions yet.</div>
          ) : (
            <div className="rounded-[15px] border border-line divide-y divide-line/60 bg-white">
              {items.map((m) => (
                <MetricRow
                  key={m.id}
                  metric={m}
                  onRemove={async () => {
                    try {
                      await deleteMetricPreference(bearer, m.id);
                      reload();
                    } catch (e) {
                      setError(e instanceof Error ? e.message : String(e));
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function MetricRow({
  metric,
  onRemove,
}: {
  metric: MetricPreference;
  onRemove: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <div className="group/m px-4 py-3 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[14px] font-medium text-neutral-900">{metric.metric_phrase}</div>
        {metric.description && (
          <div className="text-[12px] text-neutral-500 mt-0.5">{metric.description}</div>
        )}
        <div className="text-[12px] text-neutral-400 mt-1 truncate">→ {metric.rewritten_query}</div>
      </div>
      {confirming ? (
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => {
              setConfirming(false);
              onRemove();
            }}
            className="text-[12px] text-red-600 hover:underline"
          >
            Remove
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="text-[12px] text-neutral-400 hover:text-neutral-700"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="h-7 w-7 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-red-600 opacity-0 group-hover/m:opacity-100 transition-opacity shrink-0"
          aria-label="Remove metric"
          title="Remove metric"
        >
          <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" d="M3 4h10M6 4V3a1 1 0 011-1h2a1 1 0 011 1v1M5 4l1 9h4l1-9" />
          </svg>
        </button>
      )}
    </div>
  );
}
